// frontend/src/utils/reference/entityRoleAssigner.js

import {
  addRole,
  cloneEntityCandidate,
  dedupeEntities,
  limitEntities,
  sameEntity,
  sortEntitiesByScore,
} from './entityStateHelpers';

function assignEntityRoles({
  candidates = [],
  active = null,
  previous = null,
  namedKeys = [],
  historicalKeys = [],
} = {}) {
  const uniqueNamedKeys = Array.isArray(namedKeys) ? [...new Set(namedKeys)] : [];
  const uniqueHistoricalKeys = Array.isArray(historicalKeys)
    ? [...new Set(historicalKeys)]
    : [];

  const scoredCandidates = sortEntitiesByScore(dedupeEntities(candidates));

  let activeEntity = active ? addRole(cloneEntityCandidate(active), 'active') : null;
  let previousEntity =
    previous && !sameEntity(previous, activeEntity)
      ? addRole(cloneEntityCandidate(previous), 'previous')
      : null;

  const named = [];
  const related = [];
  const historical = [];

  for (const candidate of scoredCandidates) {
    let next = cloneEntityCandidate(candidate);
    if (!next) continue;

    const isActive = activeEntity && sameEntity(next, activeEntity);
    const isPrevious = previousEntity && sameEntity(next, previousEntity);
    const isNamed =
      uniqueNamedKeys.includes(next.key) || next?.signals?.explicitlyNamed === true;
    const isHistorical =
      uniqueHistoricalKeys.includes(next.key) || next?.signals?.historical === true;

    if (isActive) {
      activeEntity = addRole(
        {
          ...next,
          ...activeEntity,
          score: Math.max(next.score || 0, activeEntity.score || 0),
          signals: {
            ...(next.signals || {}),
            ...(activeEntity.signals || {}),
          },
          metadata: {
            ...(next.metadata || {}),
            ...(activeEntity.metadata || {}),
          },
        },
        'active'
      );
      if (isNamed) {
        activeEntity = addRole(activeEntity, 'named');
      }
      if (isHistorical) {
        activeEntity = addRole(activeEntity, 'historical');
      }
      continue;
    }

    if (isPrevious) {
      previousEntity = addRole(
        {
          ...next,
          ...previousEntity,
          score: Math.max(next.score || 0, previousEntity.score || 0),
          signals: {
            ...(next.signals || {}),
            ...(previousEntity.signals || {}),
          },
          metadata: {
            ...(next.metadata || {}),
            ...(previousEntity.metadata || {}),
          },
        },
        'previous'
      );
      if (isNamed) {
        previousEntity = addRole(previousEntity, 'named');
      }
      if (isHistorical) {
        previousEntity = addRole(previousEntity, 'historical');
      }
      continue;
    }

    if (isNamed) {
      next = addRole(next, 'named');
      named.push(next);
    }

    if (isHistorical) {
      next = addRole(next, 'historical');
      historical.push(next);
      continue;
    }

    next = addRole(next, 'related');
    related.push(next);
  }

  const finalNamed = limitEntities(
    sortEntitiesByScore(
      dedupeEntities([
        ...named,
        ...(activeEntity?.roles?.includes('named') ? [activeEntity] : []),
        ...(previousEntity?.roles?.includes('named') ? [previousEntity] : []),
      ])
    ),
    5
  );

  const finalRelated = limitEntities(
    sortEntitiesByScore(
      dedupeEntities(
        related.filter(
          (entity) =>
            !sameEntity(entity, activeEntity) &&
            !sameEntity(entity, previousEntity)
        )
      )
    ),
    4
  );

  const finalHistorical = limitEntities(
    sortEntitiesByScore(
      dedupeEntities(
        historical.filter(
          (entity) =>
            !sameEntity(entity, activeEntity) &&
            !sameEntity(entity, previousEntity) &&
            !finalRelated.some((relatedEntity) => sameEntity(entity, relatedEntity))
        )
      )
    ),
    8
  );

  return {
    active: activeEntity || null,
    previous: previousEntity || null,
    related: finalRelated,
    named: finalNamed,
    historical: finalHistorical,
  };
}

export { assignEntityRoles };